// Static public page: synthetic script illustrations, no account or show data or API calls.
import { useEffect } from "preact/hooks";

export function Logo() {
  return (
    <span class="logo" aria-label="q2cue" role="img">
      q<span class="logo-2">2</span>cue
    </span>
  );
}

function LandingHeader({ signInHref = "/signin" }: { signInHref?: string }) {
  return (
    <header class="landing-head">
      <div class="landing-wrap landing-bar">
        <a href="/#top" class="brand"><Logo /></a>
        <nav class="landing-nav" aria-label="Sections">
          <a href="/#how">How it works</a>
          <a href="/#what">What you get</a>
          <a href="/#details">At the desk</a>
          <a href="/#self-hosting">Self-hosting</a>
        </nav>
        <a class="button" href={signInHref}>Sign in</a>
      </div>
    </header>
  );
}

/** Public chrome only while account resolution decides which page to open. */
export function StartupLoading({ signInHref }: { signInHref: string }) {
  return (
    <div class="landing" id="top">
      <a class="landing-skip" href="#main">Skip to content</a>
      <LandingHeader signInHref={signInHref} />
      <main id="main" tabIndex={-1} class="landing-wrap landing-loading" aria-busy="true">
        <p role="status">Loading…</p>
      </main>
    </div>
  );
}

/** An illustrative prompt-book page, not an interactive editor or a real script. */
function PromptPage() {
  const lines: [number, number, string][] = [
    [220, 70, "ACT TWO, SCENE 3"],
    [126, 104, "The signal room. Night. A single lamp burns"],
    [126, 119, "on the desk. MARA sits with the logbook open."],
    [126, 134, "The wind has dropped."],
    [270, 171, "MARA"],
    [166, 191, "Forty-one days. Same entry every night."],
    [166, 206, '"No vessels sighted."'],
    [126, 242, "She closes the logbook. A long beat."],
    [244, 279, "MARA (CONT'D)"],
    [166, 299, "Tomorrow I'll write something else."],
    [126, 335, "The door opens. ELLIS enters, coat soaked,"],
    [126, 350, "carrying a lantern that has gone out."],
    [268, 387, "ELLIS"],
    [166, 407, "Lamp's dead. No spare oil till morning."],
    [126, 449, "A low rumble from offstage. Both look up."],
    [126, 486, "The light outside sweeps across them once,"],
    [126, 501, "then again."],
    [126, 543, "Blackout."],
  ];
  return (
    <svg class="prompt-page" viewBox="0 0 540 580" role="img" aria-label="Illustrative prompt book, page 48. Lighting, sound and video cues and a standby connect to marks on a script.">
      <text class="page-number" x="494" y="34">48.</text>
      <g class="script-text">
        {lines.map(([x, y, text]) => <text x={x} y={y}>{text}</text>)}
      </g>
      <g class="binder-holes" aria-hidden="true">
        {[90, 290, 490].map((y) => <circle cx="10" cy={y} r="6" />)}
      </g>
      <g class="page-marks" fill="none" stroke-width="1.5" aria-hidden="true">
        <path d="M 214 149 H 100" stroke="var(--paper-ink)" stroke-dasharray="3 3" />
        <circle cx="214" cy="149" r="4" stroke="var(--paper-ink)" />
        <path d="M 91 239 H 116" stroke="var(--cue-lx)" />
        <circle cx="120" cy="239" r="4" fill="var(--cue-lx)" stroke="var(--cue-lx)" />
        <path d="M 91 445 H 122 M 104 445 V 469 H 91" stroke="var(--cue-sfx)" />
        <rect x="122" y="435" width="386" height="19" rx="2" stroke="var(--cue-lx)" />
        <path d="M 91 445 H 122" stroke="var(--cue-lx)" />
        <path d="M 91 491 H 122" stroke="var(--cue-vfx)" />
        <rect x="122" y="472" width="386" height="35" rx="2" stroke="var(--cue-vfx)" />
        <path d="M 91 540 H 116" stroke="var(--cue-lx)" />
        <circle cx="120" cy="540" r="4" stroke="var(--cue-lx)" />
      </g>
      <g class="page-cue standby">
        <rect x="28" y="138" width="70" height="22" rx="2" />
        <text x="36" y="153">STANDBY</text>
      </g>
      {([
        [228, "LX 14", "lx"],
        [434, "LX 15", "lx"],
        [458, "SFX 7", "sfx"],
        [480, "VFX 3", "vfx"],
        [529, "LX 16", "lx"],
      ] as const).map(([y, text, department]) => (
        <g class={`page-cue ${department}`}>
          <rect x="28" y={y} width="62" height="22" rx="2" />
          <text x="36" y={y + 15}>{text}</text>
        </g>
      ))}
    </svg>
  );
}

function ScriptThumb({ department = false }: { department?: boolean }) {
  return (
    <svg viewBox="0 0 240 230" role="img" aria-label={department ? "Department script showing only lighting cues" : "Prompt book with all departments' cues in the outer margin"}>
      <text class="thumb-title" x="28" y="28">{department ? "LIGHTING SCRIPT" : "PROMPT BOOK"}</text>
      <text class="page-number" x="205" y="28">48.</text>
      <g class="thumb-lines" stroke-width="3">
        {[52, 62, 72, 98, 108, 118, 144, 154, 164, 190, 200].map((y, i) => (
          <path d={`M 87 ${y} h ${[118, 99, 108, 82][i % 4]}`} />
        ))}
      </g>
      {([
        [47, "LX 14", "lx"],
        [93, "LX 15", "lx"],
        [116, "SFX 7", "sfx"],
        [149, "VFX 3", "vfx"],
        [185, "LX 16", "lx"],
      ] as const).filter(([, , dept]) => !department || dept === "lx").map(([y, text, dept]) => (
        <g class={`page-cue ${dept}`}>
          <rect x="18" y={y} width="54" height="18" rx="2" />
          <text x="24" y={y + 12.5}>{text}</text>
        </g>
      ))}
    </svg>
  );
}

function CueSheetThumb() {
  const rows = [
    ["LX 14", "lx", "Desk lamp dims"],
    ["LX 15", "lx", "Cold blue wash"],
    ["SFX 7", "sfx", "Distant foghorn"],
    ["VFX 3", "vfx", "Beam sweep, twice"],
    ["LX 16", "lx", "Snap to black"],
  ];
  return (
    <svg viewBox="0 0 286 230" role="img" aria-label="Cue sheet with cue numbers, script pages and descriptions">
      <text class="thumb-title" x="18" y="28">CUE SHEET / ACT TWO</text>
      <g class="sheet-heading"><text x="18" y="53">CUE</text><text x="81" y="53">PAGE</text><text x="128" y="53">DESCRIPTION</text></g>
      {rows.map(([cue, dept, text], i) => (
        <g class="sheet-row">
          <path d={`M 18 ${64 + i * 29} H 268`} />
          <text class={dept} x="18" y={83 + i * 29}>{cue}</text>
          <text class="sheet-page" x="81" y={83 + i * 29}>48</text>
          <text x="128" y={83 + i * 29}>{text}</text>
        </g>
      ))}
    </svg>
  );
}

const STEPS = [
  ["Open your script", "Upload your script PDF and set its printed page numbers, even if they differ from the PDF page numbers."],
  ["Add cues and notes", "With a mouse, choose Place, then click for a pin or drag to mark a passage. With Apple Pencil, tap or drag directly. Fingers scroll and pinch to zoom without placing marks."],
  ["Make the paperwork", "Export your prompt book, department scripts and cue sheets. After changing cues or notes, export again to update the copies."],
] as const;

const DETAILS = [
  ["Work offline", 'Open the show online and wait for "Ready for offline use" in Save status. You can then read and mark without a connection. Save status shows edits waiting to sync or needing review.'],
  ["Save a checkpoint", "Name a saved version before a big change in tech. The show's owner can restore it without losing the later history."],
  ["Filter departments and notes", "Start with LX, SFX, FLY and VFX, or add your own. Keep Director, Blocking and Rewrites notes on layers you can show or hide."],
  ["Keep the source PDF unchanged", "Cues and notes are separate data laid over your script. The source file is never changed, including when you export a prompt book."],
  ["Number cues and group shared GOs", "Use decimals and letter suffixes for cue numbers. Lighting and sound at the same moment stay together as a multi-cue."],
  ["Share the show", "Invite people to edit or view the show. To move a copy to another q2cue installation, export a bundle containing the script and annotations."],
] as const;

export function Landing() {
  useEffect(() => {
    document.title = "q2cue · Mark cues on your script PDF.";
  }, []);
  return (
    <div class="landing" id="top">
      <a class="landing-skip" href="#main">Skip to content</a>
      <LandingHeader />
      <main id="main" tabIndex={-1}>
        <section class="landing-wrap hero" aria-labelledby="hero-heading">
          <div class="hero-copy">
            <p class="eyebrow hero-eyebrow"><span aria-hidden="true" />For the stage manager</p>
            <h1 id="hero-heading">Mark cues on your script PDF.</h1>
            <p class="lede">Use a mouse or Apple Pencil to add cues, standbys and notes. Export a prompt book, department scripts and cue sheets from the same annotations.</p>
            <div class="row-buttons">
              <a class="button primary" href="/signin">Sign up <span aria-hidden="true">↗</span></a>
              <a class="hero-how" href="#how">See how it works <span aria-hidden="true">↓</span></a>
            </div>
            <p class="hero-note">Use q2cue on a computer or iPad. Runs in your browser, with no app to install.</p>
          </div>
          <figure class="hero-figure">
            <div class="hero-paper"><PromptPage /></div>
            <figcaption><span>Cues and a standby on a sample script.</span><span>Illustrative prompt book</span></figcaption>
          </figure>
        </section>
        <section class="landing-section" id="how" aria-labelledby="how-heading">
          <div class="landing-wrap">
            <div class="section-heading"><p class="eyebrow">01 / The process</p><h2 id="how-heading">From script to prompt book.</h2></div>
            <ol class="steps">
              {STEPS.map(([title, text], i) => (
                <li><span class="step-n" aria-hidden="true">0{i + 1}.</span><h3>{title}</h3><p>{text}</p></li>
              ))}
            </ol>
          </div>
        </section>
        <section class="landing-section outputs-section" id="what" aria-labelledby="what-heading">
          <div class="landing-wrap">
            <div class="section-heading"><p class="eyebrow">02 / The paperwork</p><h2 id="what-heading">Prompt books, department scripts and cue sheets</h2><p>Update the annotations in q2cue, then export the copies you need.</p></div>
            <div class="outputs">
              <article>
                <div class="output-preview"><div class="output-paper"><ScriptThumb /></div><span class="output-tab">ALL CUES</span></div>
                <h3>Prompt book</h3>
                <p>Your script with cues, standbys and notes. Margin labels sit on the outside edge for double-sided printing.</p>
              </article>
              <article>
                <div class="output-preview"><div class="output-paper"><ScriptThumb department /></div><span class="output-tab lx">LX ONLY</span></div>
                <h3>Department scripts</h3>
                <p>The same script, filtered for the department that needs it. Lighting gets the LX cues, with the numbers they already use.</p>
              </article>
              <article>
                <div class="output-preview"><div class="output-paper cue-sheet-paper"><CueSheetThumb /></div><span class="output-tab">PDF + CSV</span></div>
                <h3>Cue sheets</h3>
                <p>Cue numbers, pages and descriptions in script order. Export one department's list or keep everything together.</p>
              </article>
            </div>
          </div>
        </section>
        <section class="landing-section" id="details" aria-labelledby="details-heading">
          <div class="landing-wrap desk-layout">
            <div class="section-heading"><p class="eyebrow">03 / At the desk</p><h2 id="details-heading">During rehearsals and tech</h2><p>Keep marking when the connection drops, and return to earlier saved versions when you need to.</p></div>
            <dl class="details">
              {DETAILS.map(([title, text]) => <div><dt>{title}</dt><dd>{text}</dd></div>)}
            </dl>
          </div>
        </section>
        <section class="landing-section" id="self-hosting" aria-labelledby="self-hosting-heading">
          <div class="landing-wrap desk-layout">
            <div class="section-heading"><p class="eyebrow">04 / Open source</p><h2 id="self-hosting-heading">Run your own q2cue.</h2></div>
            <div class="closing-actions">
              <p>q2cue is open source. Deploy an independent installation in your own Cloudflare account, with your own domain and sign-in email setup.</p>
              <a class="button" href="https://gitlab.com/brendan/q2q/-/blob/main/docs/hosted/self-hosting.md">Self-hosting guide <span aria-hidden="true">↗</span></a>
              <a class="import-link" href="https://gitlab.com/brendan/q2q">View source on GitLab <span aria-hidden="true">↗</span></a>
            </div>
          </div>
        </section>
        <section class="closing">
          <div class="landing-wrap closing-layout">
            <div><p class="eyebrow">Your next show</p><h2>Start with the script<br />you already have.</h2><p>Sign in with Google or an emailed link.</p></div>
            <div class="closing-actions"><a class="button primary" href="/signin">Sign in <span aria-hidden="true">↗</span></a><a class="import-link" href={`/signin?return=${encodeURIComponent("/?import")}`}>Already have a bundle? Import it <span aria-hidden="true">→</span></a></div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

/** Footer for the public pages: the homepage, privacy and terms (each wraps itself in id="top"). */
export function SiteFooter() {
  return (
    <footer class="landing-wrap landing-foot">
      <a class="brand" href="/"><Logo /></a><span class="footer-note">The script stays yours.</span>
      <a href="/privacy">Privacy</a>
      <a href="/terms">Terms</a>
      <a href="https://gitlab.com/brendan/q2q">Open source <span aria-hidden="true">↗</span></a>
      <a href="#top">Back to top ↑</a>
    </footer>
  );
}
